import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';

import { ignore } from '@utils/object';
import Shell from '@shared/containers/Shell';
import Cart from '@cart/containers/Cart';
import { VERTICAL_GENDER_CONTRACTION_OF } from '@conversion/constants';
import ShopHeader from '@shopDetails/components/ShopHeader/ExtendedMenuHeader';
import DesktopShelvesMenuHeader from '@shopDetails/components/ShopHeader/DesktopShelvesMenuHeader';
import ColumnMenuSection from '@shopDetails/components/ColumnMenuSection/Loadable';

import Modal from '@pedidosya/web-fenix/system/Modal';
import Button from '@pedidosya/web-fenix/system/Button';

import StandardFooter from '@shared/containers/Footer';
import isStickyPositionSupported from '@utils/featureDetection/cssPositionSticky';
import CartContainer from './components/CartContainer';

import messages from './messages';
import ContainerShimmer from '../../components/Pickup/DesktopPickupToggle/components/ContainerShimmer';

const PROPS = {
  // data
  shop: PropTypes.object,
  cartHasItems: PropTypes.bool.isRequired,
  isFetchingShop: PropTypes.bool.isRequired,
  hasFetchedMenu: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
  isDesktopLayout: PropTypes.bool,
  vertical: PropTypes.shape({
    gender: PropTypes.string,
    plural: PropTypes.string,
    position: PropTypes.number,
    name: PropTypes.string,
    singular: PropTypes.string,
    icon: PropTypes.string,
    businessType: PropTypes.string,
    id: PropTypes.number,
    image: PropTypes.string,
  }).isRequired,
  isShelvesMenu: PropTypes.bool,
  fallbackUrl: PropTypes.string.isRequired,
  isFetchingMenu: PropTypes.bool.isRequired,
  menu: PropTypes.object,
  countryCode: PropTypes.string.isRequired,

  // WithOnBeforeProductConfig
  onBeforeProductConfig: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  show: PropTypes.bool.isRequired,

  // WithAgeValidation
  hasToValidateAge: PropTypes.bool.isRequired,
  openAgeValidation: PropTypes.func.isRequired,

  // callbacks
  goBack: PropTypes.func.isRequired,
  trackBackButtonClicked: PropTypes.func.isRequired,
  isShopDetailsClosingMsgFlag: PropTypes.bool.isRequired,
};

export default function WithDesktopShopDetails(T) {
  return class WithShopDetailsFn extends PureComponent {
    static propTypes = PROPS;

    state = {
      showNavigationPrompt: false,
    };

    isMounted = false;

    componentDidMount() {
      this.isMounted = true;
    }

    componentWillUnmount() {
      this.isMounted = false;
    }

    setStateIfMounted = newState => {
      if (this.isMounted) this.setState(newState);
    };

    onCancelGoBackModal = () =>
      this.setStateIfMounted({ showNavigationPrompt: false });

    onGoBack = ({ hasItemsChecked = false }) => {
      const {
        cartHasItems,
        fallbackUrl,
        goBack,
        trackBackButtonClicked,
      } = this.props;
      trackBackButtonClicked();
      if (!cartHasItems || hasItemsChecked) {
        this.setStateIfMounted({ showNavigationPrompt: false });
        goBack({ fallbackUrl });
      } else {
        this.setStateIfMounted({ showNavigationPrompt: true });
      }
    };

    shouldShowShimmer() {
      const { isFetchingShop, hasFetchedMenu } = this.props;
      return Boolean(isFetchingShop || !hasFetchedMenu);
    }

    getPrefix = () => {
      const { vertical } = this.props;
      return VERTICAL_GENDER_CONTRACTION_OF[vertical.gender];
    };

    getPropsToInject() {
      return ignore(this.props, Object.keys(PROPS));
    }

    headerComponent = () => {
      const { shop, isShelvesMenu } = this.props;
      return isShelvesMenu ? (
        <DesktopShelvesMenuHeader shop={shop} goBack={this.onGoBack} />
      ) : (
        <ShopHeader
          goBack={this.onGoBack}
          shop={shop}
          showShimmer={this.shouldShowShimmer()}
        />
      );
    };

    leftComponent = () => {
      const {
        intl: { formatMessage },
      } = this.props;
      return (
        <>
          <div role="complementary">
            {this.shouldShowShimmer() ? (
              <ContainerShimmer height={340} />
            ) : (
              <ColumnMenuSection
                title={formatMessage(messages.menuSection)}
                {...this.props}
              />
            )}
          </div>
        </>
      );
    };

    render() {
      const { show, hasToValidateAge } = this.props;
      if (!show) return null;

      const {
        shop,
        menu,
        isFetchingMenu,
        countryCode,
        intl: { formatMessage },
        vertical,
        loading,
        onBeforeProductConfig,
        openAgeValidation,
      } = this.props;

      const { showNavigationPrompt } = this.state;

      const closeDialogConfig = {
        title: formatMessage(messages.deleteCartModal_new_message),
        text: formatMessage(messages.deleteCartModal_new_title, {
          businessType: vertical.singular,
          prefix: this.getPrefix(),
        }),
        firstButtonText: formatMessage(messages.deleteCartModal_new_accept),
        secondButtonText: formatMessage(messages.deleteCartModal_new_cancel),
      };

      return (
        <>
          <Shell
            showOverlaySpinner={loading}
            rightColumn={
              <CartContainer
                role="complementary"
                aria-label={formatMessage(messages.cartSectionAriaLabel)}
                stickySupported={isStickyPositionSupported()}
              >
                {this.shouldShowShimmer() ? (
                  <ContainerShimmer height={214} />
                ) : (
                  <Cart />
                )}
              </CartContainer>
            }
            header={this.headerComponent()}
            footer={<StandardFooter />}
            leftColumn={this.leftComponent()}
          >
            <T
              shop={shop}
              menu={menu}
              hasToValidateAge={hasToValidateAge}
              showShimmer={this.shouldShowShimmer()}
              openAgeValidation={openAgeValidation}
              isFetchingMenu={isFetchingMenu}
              countryCode={countryCode}
              onGoBack={this.onGoBack}
              onBeforeProductConfig={onBeforeProductConfig}
              {...this.getPropsToInject()}
            />

            <Modal
              open={showNavigationPrompt}
              onClose={this.onCancelGoBackModal}
              primaryButton={
                <Button
                  fullWidth
                  label={closeDialogConfig.firstButtonText}
                  size="large"
                  onClick={() => {
                    this.onGoBack({ hasItemsChecked: true });
                  }}
                />
              }
              secondaryButton={
                <Button
                  fullWidth
                  hierarchy="secondary"
                  label={closeDialogConfig.secondButtonText}
                  size="large"
                  onClick={this.onCancelGoBackModal}
                />
              }
              text={closeDialogConfig.text}
              title={closeDialogConfig.title}
            >
              {closeDialogConfig.image}
            </Modal>
          </Shell>
        </>
      );
    }
  };
}
